// ─────────────────────────────────────────────────────────────────────────────
// UnlockManager — routes purchase/restore to the correct platform adapter
//
// Platform detection order:
//   1. Capacitor native  → 'android' | 'ios'
//   2. Steamworks         → 'steam'
//   3. MS Store API       → 'msstore'
//   4. Everything else    → 'web' (Stripe)
//
// Each adapter must implement:
//   { purchase(), restore(), isUnlocked() }
// ─────────────────────────────────────────────────────────────────────────────

import { PaymentService } from './PaymentService.js';

// Lazy adapter imports — only the active platform's adapter is loaded
const ADAPTERS = {
  android:  () => import('./adapters/GooglePlayAdapter.js').then(m => m.default),
  ios:      () => import('./adapters/AppleAdapter.js').then(m => m.default),
  steam:    () => import('./adapters/SteamAdapter.js').then(m => m.default),
  msstore:  () => import('./adapters/MSStoreAdapter.js').then(m => m.default),
  web:      () => import('./adapters/WebStripeAdapter.js').then(m => m.default),
};

let _platform = null;
let _adapter  = null;

/** Detect which platform we're running on. */
function detectPlatform() {
  if (_platform) return _platform;

  // Capacitor native app (Android / iOS)
  if (window.Capacitor?.isNativePlatform?.()) {
    _platform = window.Capacitor.getPlatform(); // 'android' | 'ios'
    return _platform;
  }

  // Steamworks.js (Electron + Steam overlay)
  if (window.steamworks) {
    _platform = 'steam';
    return _platform;
  }

  // MS Store (MSIX / PWA with Windows Store API)
  if (window.Windows?.ApplicationModel?.Store) {
    _platform = 'msstore';
    return _platform;
  }

  // Default: web browser → Stripe
  _platform = 'web';
  return _platform;
}

/** Get (and cache) the adapter for the current platform. */
async function getAdapter() {
  if (_adapter) return _adapter;

  const platform = detectPlatform();
  const loader = ADAPTERS[platform];

  if (!loader) {
    console.warn(`[UnlockManager] No adapter for platform "${platform}", falling back to web`);
    _adapter = await ADAPTERS.web();
    return _adapter;
  }

  try {
    _adapter = await loader();
  } catch (err) {
    console.warn(`[UnlockManager] Failed to load ${platform} adapter, falling back to web:`, err);
    _adapter = await ADAPTERS.web();
  }

  return _adapter;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export const UnlockManager = {
  /** Current detected platform. */
  getPlatform() {
    return detectPlatform();
  },

  /** Display price adjusted for platform commission. */
  getPriceDisplay() {
    const p = detectPlatform();
    // Google Play / Apple take 30% — pass savings to user
    if (p === 'android' || p === 'ios') return '€3.99';
    return '€4.99';
  },

  /**
   * Is the full game already unlocked?
   * Checks localStorage first (fast), then asks the adapter for server truth.
   */
  isUnlocked() {
    return PaymentService.isUnlocked();
  },

  /**
   * Initiate a purchase through the platform's native payment system.
   * Returns { success: boolean, pending?: boolean, error?: string }
   */
  async purchase() {
    const adapter = await getAdapter();
    const result = await adapter.purchase();

    if (result.success) {
      PaymentService.grantUnlock();
    }

    return result;
  },

  /**
   * Restore a previous purchase (required by Apple, good practice everywhere).
   * Returns { success: boolean, error?: string }
   */
  async restore() {
    const adapter = await getAdapter();
    const result = await adapter.restore();

    if (result.success) {
      PaymentService.grantUnlock();
    }

    return result;
  },

  /**
   * Handle URL-based callbacks (Stripe redirect).
   * Call once on game startup.
   */
  handleUrlCallback() {
    return PaymentService.handleUrlCallback();
  },

  /** Dev helper — revoke unlock for testing. */
  _devRevoke() {
    PaymentService._devRevoke();
  },
};
